import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Award } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const txt = (lang, fr, ru, md, uk) => ({ fr, ru, md, uk }[lang] || fr);

// CSS keyframes for fireworks
const fireworkStyles = `
@keyframes firework-burst {
  0% { transform: scale(0); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.8; }
  100% { transform: scale(0); opacity: 0; }
}
@keyframes firework-particle {
  0% { transform: translate(0, 0) scale(1); opacity: 1; }
  100% { transform: translate(var(--tx), var(--ty)) scale(0); opacity: 0; }
}
@keyframes confetti-fall {
  0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
  100% { transform: translateY(100px) rotate(720deg); opacity: 0; }
}
@keyframes pop-in {
  0% { transform: scale(0.5); opacity: 0; }
  60% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
}
`;

function Particle({ delay, color, tx, ty }) {
  return (
    <div
      className="absolute w-2 h-2 rounded-full"
      style={{
        backgroundColor: color,
        '--tx': `${tx}px`,
        '--ty': `${ty}px`,
        animation: `firework-particle 1.2s ease-out ${delay}s forwards`,
        left: '50%',
        top: '40%',
      }}
    />
  );
}

function Confetti({ delay, color, left }) {
  return (
    <div
      className="absolute w-2 h-3 rounded-sm"
      style={{
        backgroundColor: color,
        left: `${left}%`,
        top: '-5px',
        animation: `confetti-fall 2s ease-in ${delay}s forwards`,
      }}
    />
  );
}

export default function BonusCelebration() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [bonusNotif, setBonusNotif] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user || user.role === 'manager' || user._isPreview) return;

    const checkBonus = async () => {
      try {
        const res = await axios.get(`${API}/notifications`);
        const notifs = res.data || [];
        // Find unread bonus notification
        const bonus = notifs.find(n => n.action === 'bonus_added' && !n.is_read && n.target_user_id === user.id);
        if (bonus) {
          setBonusNotif(bonus);
          setShow(true);
          // Mark as read
          axios.post(`${API}/notifications/${bonus.id}/read`).catch(() => {});
        }
      } catch {}
    };

    const timer = setTimeout(checkBonus, 1500);
    return () => clearTimeout(timer);
  }, [user]);

  if (!show || !bonusNotif) return null;

  const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#FF8C00'];
  const particles = [];
  for (let i = 0; i < 24; i++) {
    const angle = (i / 24) * Math.PI * 2;
    const dist = 60 + Math.random() * 40;
    particles.push({
      tx: Math.cos(angle) * dist,
      ty: Math.sin(angle) * dist,
      color: colors[i % colors.length],
      delay: Math.random() * 0.3,
    });
  }

  const confettis = [];
  for (let i = 0; i < 20; i++) {
    confettis.push({
      left: Math.random() * 100,
      color: colors[i % colors.length],
      delay: Math.random() * 1,
    });
  }

  return (
    <>
      <style>{fireworkStyles}</style>
      <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShow(false)}>
        {/* Confetti */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {confettis.map((c, i) => (
            <Confetti key={i} {...c} />
          ))}
        </div>

        {/* Main popup */}
        <div
          className="relative bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-sm mx-4 text-center overflow-hidden"
          style={{ animation: 'pop-in 0.5s ease-out forwards' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Particles */}
          <div className="absolute inset-0 pointer-events-none">
            {particles.map((p, i) => (
              <Particle key={i} {...p} />
            ))}
          </div>

          {/* Trophy icon */}
          <div className="relative mx-auto w-20 h-20 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg mb-4">
            <Award className="w-10 h-10 text-white" />
          </div>

          {/* Text */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            {txt(language, 'Félicitations !', 'Поздравляем!', 'Felicitări!', 'Вітаємо!')}
          </h2>
          <p className="text-base text-gray-600 mb-1">
            {txt(language, 'Vous avez reçu une prime', 'Вы получили премию', 'Ați primit o primă', 'Ви отримали премію')}
          </p>
          <p className="text-3xl font-bold text-emerald-600 mb-4">
            {bonusNotif.title}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {bonusNotif.message}
          </p>

          <Button onClick={() => setShow(false)} className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold text-base py-3">
            {txt(language, 'Merci !', 'Спасибо!', 'Mulțumesc!', 'Дякую!')}
          </Button>
        </div>
      </div>
    </>
  );
}
